/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './RecipientList.scss';

import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { DetailsList, IColumn, IDetailsList, IDragDropEvents } from '@fluentui/react/lib/DetailsList';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { IStackTokens, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { TextField } from '@fluentui/react/lib/TextField';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { CustomizerContext, SelectionMode } from '@fluentui/react/lib/Utilities';
import { useBoolean, useConst } from '@uifabric/react-hooks';

import * as React from 'react';
import { ApprovalPersonaProps } from '../../../models/ApprovalPersonaProps';
import { IconName } from '../../../resources/icons/Icons';
import { getEsignRecipientSectionLabelStyles } from '../../../styles/custom/ESignRecipientSectionLabelStyles';
import { getSectionLabelStyles } from '../../../styles/teams/styles/Label.styles';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { stringFormat } from '../../../utilities/Utils';
import { ApproverPeoplePicker } from '../../components/ApproverPeoplePicker';
import { getDetailsListStyles } from './RecipientList.styles';
import { copy, getRecipientTypeAriaLabel, getRecipientTypeLabel, Recipient, RecipientTypeLabel } from './index';

export interface RecipientListComponentProps {
    recipients?: Recipient[];
    availableRoles?: string[];
    isAddRecipientDisabled?: boolean;
    completeInOrder?: boolean;
    toggleCompleteInOrder?: () => void;
    moveRecipient?: (recipient: Recipient, toRecipient: Recipient) => void;
    moveRecipientWithOrder?: (recipient: Recipient, order: string) => void;
    updateRecipient?: (recipient: Recipient, newRecipient: Recipient) => void;
    addRecipient?: (ref?: React.RefObject<IDetailsList>) => void;
    removeRecipient?: (recipient: Recipient) => void;
    required?: boolean;
    recipientTypeLabel?: RecipientTypeLabel;
    showNameField?: boolean;
}

export const RecipientListComponent: React.FC<RecipientListComponentProps> = (
    props: React.PropsWithChildren<RecipientListComponentProps>
): JSX.Element => {
    const {
        recipients,
        availableRoles,
        isAddRecipientDisabled,
        moveRecipient,
        moveRecipientWithOrder,
        updateRecipient,
        addRecipient,
        removeRecipient,
        completeInOrder,
        toggleCompleteInOrder,
        required,
        recipientTypeLabel,
        showNameField,
    } = props;

    const loc = getAppHost().getLocale();
    const {
        recipient_list_label,
        recipient_list_label_complete_in_order,
        recipient_list_button_text_add_recipient,
        recipient_list_drag_handle_aria_label,
        recipient_list_signing_order_aria_label,
        people_picker_placeholder_text_reassign,
        recipient_list_name_aria_label,
        recipient_list_remove_recipient_aria_label,
        recipient_grid_label,
    } = loc.strings;

    const validateOrder = (order: string): boolean => /^[0-9]*$/.test(order);
    const themeName = getAppHost().getTheme();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const [draggable, { setTrue: setDraggableTrue, setFalse: setDraggableFalse }] = useBoolean(false);
    const roleOptions = useConst(
        (availableRoles || []).map(
            (option: string, index: number): IDropdownOption => ({
                key: index,
                text: option,
            })
        )
    );

    const emailLabelId = 'email-label';

    let draggedItem: Recipient;

    // Render drag handle for moving a recipient
    const onRenderDragHandle = (): JSX.Element => (
        <Icon
            iconName='GripperDotsVertical'
            aria-label={recipient_list_drag_handle_aria_label}
            className='drag-handle'
            onMouseDown={setDraggableTrue}
            onMouseLeave={setDraggableFalse}
        />
    );

    // Render recipient order input
    const onRenderOrder = (item: Recipient): JSX.Element => {
        // On order changes, update the order only if it's a valid number
        const onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
            const newValue = evt.target.value;

            if (validateOrder(newValue)) {
                updateRecipient(item, copy(item, newValue));
            }
        };

        // On order blur, move the recipient accordingly
        const onBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
            const newValue = evt.target.value;

            if (validateOrder(newValue)) {
                moveRecipientWithOrder(item, newValue);
            }
        };

        return (
            <TextField
                className='order-input'
                ariaLabel={recipient_list_signing_order_aria_label}
                value={item.order}
                type='text'
                maxLength={2}
                onChange={onChange}
                onBlur={onBlur}
            />
        );
    };

    // On render role dropdown
    const onRenderRole = (item: Recipient): JSX.Element => {
        const value = item.role;

        // On dropdown selection change, update the recipient role
        const onChange = (evt: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
            if (option.key !== value)
                updateRecipient(item, {
                    ...copy(item),
                    role: option.key as number,
                });
        };

        return (
            <Dropdown
                className='role-dropdown'
                aria-label={getRecipientTypeAriaLabel(recipientTypeLabel, roleOptions[value]?.text)}
                selectedKey={value}
                options={roleOptions}
                onChange={onChange}
                responsiveMode={ResponsiveMode.large}
            />
        );
    };

    const onRenderName = (item: Recipient): JSX.Element => {
        const onChange = (newValue: string): void => {
            updateRecipient(item, {
                ...copy(item),
                name: newValue,
            });
        };

        return (
            <TextField
                className='name-input'
                ariaLabel={recipient_list_name_aria_label}
                placeholder={people_picker_placeholder_text_reassign}
                value={item.name}
                type='text'
                maxLength={200}
                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void =>
                    onChange(newValue)
                }
            />
        );
    };

    // On render email input, use people picker with free email input
    const onRenderEmail = (item: Recipient): JSX.Element => {
        // On person/email selected, update the recipient and name fields
        const onApproversSelected = (approvers: ApprovalPersonaProps[]): void => {
            const newRecipient = {
                ...copy(item),
                persona: approvers && approvers.length > 0 ? approvers[0] : null,
            };

            if (showNameField) {
                newRecipient.name = approvers[0]?.email !== approvers[0]?.text ? approvers[0].text : item.name;
            }

            updateRecipient(item, newRecipient);
        };
        // On this input focus, propagate the focus to the people picker in details list
        const onEmailInputFocus = (): void => {
            if (item.emailRef && item.emailRef.current) {
                item.emailRef.current.focus();
            }
        };

        return (
            <div className='email-input' data-is-focusable aria-labelledby={emailLabelId} onFocus={onEmailInputFocus}>
                <ApproverPeoplePicker
                    onApproversSelected={onApproversSelected}
                    error={null}
                    currentSelectedItems={item.persona ? [item.persona] : []}
                    hideLabel={true}
                    enableEmail={true}
                    itemLimit={1}
                    innerRef={item.emailRef}
                    targetComponent={'.ms-BasePicker-input'}
                    required
                    mobileSelectedIcon={IconName.mail}
                    mobileUnselectedIcon={IconName.mail}
                />
            </div>
        );
    };

    // On render remove button
    const onRenderRemoveButton = (item: Recipient): JSX.Element => {
        const onClick = (): void => {
            removeRecipient(item);
        };

        return (
            <IconButton
                className='remove-button'
                iconProps={{
                    iconName: 'Cancel',
                }}
                ariaLabel={recipient_list_remove_recipient_aria_label}
                onClick={onClick}
            />
        );
    };

    const columns: IColumn[] = [
        {
            key: 'role',
            name: stringFormat(loc.strings.required_label, {
                label: loc.strings.recipient_list_column_name_role,
            }),
            minWidth: 100,
            maxWidth: 100,
            fieldName: 'role',
            onRender: onRenderRole,
        },
        {
            key: 'email',
            name: stringFormat(loc.strings.required_label, { label: loc.strings.recipient_list_column_name_email }),
            minWidth: 100,
            fieldName: 'email',
            className: 'approver-cell',
            onRender: onRenderEmail,
        },
        {
            key: 'remove',
            name: null,
            minWidth: 28,
            maxWidth: 28,
            className: 'remove-cell',
            onRender: onRenderRemoveButton,
        },
    ];

    if (showNameField) {
        columns.splice(1, 0, {
            key: 'name',
            name: stringFormat(loc.strings.required_label, { label: loc.strings.recipient_list_column_name_name }),
            minWidth: 100,
            maxWidth: 100,
            fieldName: 'name',
            onRender: onRenderName,
        });
    }

    // Insert drag handle and order column only if completeInOrder is enabled
    if (completeInOrder) {
        columns.splice(
            0,
            0,
            {
                key: 'dragHandle',
                name: null,
                minWidth: 14,
                maxWidth: 14,
                className: 'drag-handle-cell',
                onRender: onRenderDragHandle,
            },
            {
                key: 'order',
                name: null,
                minWidth: 32,
                maxWidth: 32,
                onRender: onRenderOrder,
            }
        );
    }

    // Details list properties for drag and drop events
    const getDragDropEvents = (draggable: boolean): IDragDropEvents => ({
        canDrop: (): boolean => draggable,
        canDrag: (): boolean => draggable,
        onDragEnter: (): string => 'drop-target',
        onDrop: (item: Recipient): void => {
            if (draggedItem) {
                moveRecipient(draggedItem, item);
            }
        },
        onDragStart: (item: Recipient): void => {
            draggedItem = item;
        },
        onDragEnd: (): void => {
            draggedItem = null;
        },
    });

    // Disable virtualization of the list
    const onShouldVirtualize = (): boolean => false;

    const stackSpacing: IStackTokens = {
        childrenGap: 's',
        padding: completeInOrder ? 'm 86px 0 88px' : 'm 86px m 0',
    };

    const emailSpacing: IStackTokens = {
        childrenGap: 's',
        padding: 'm 76px 0 76px',
    };

    const listRef = React.createRef<IDetailsList>();

    const recipientList = recipients && recipients.length > 0 && (
        <div>
            <Stack horizontal>
                <StackItem tokens={stackSpacing}>
                    <Label styles={getEsignRecipientSectionLabelStyles(themeName, theme)}>
                        {stringFormat(loc.strings.required_label, {
                            label: getRecipientTypeLabel(recipientTypeLabel),
                        })}
                    </Label>
                </StackItem>
                {showNameField && (
                    <StackItem>
                        <Label styles={getEsignRecipientSectionLabelStyles(themeName, theme)}>
                            {stringFormat(loc.strings.required_label, {
                                label: loc.strings.recipient_list_column_name_name,
                            })}
                        </Label>
                    </StackItem>
                )}
                <StackItem tokens={emailSpacing}>
                    <Label id={emailLabelId} styles={getEsignRecipientSectionLabelStyles(themeName, theme)}>
                        {stringFormat(loc.strings.required_label, {
                            label: loc.strings.recipient_list_column_name_email,
                        })}
                    </Label>
                </StackItem>
            </Stack>
            <DetailsList
                className={'people-list'}
                ariaLabelForGrid={recipient_grid_label}
                columns={columns}
                items={recipients}
                selectionMode={SelectionMode.none}
                dragDropEvents={getDragDropEvents(draggable)}
                isHeaderVisible={false}
                onColumnHeaderClick={null}
                onShouldVirtualize={onShouldVirtualize}
                styles={getDetailsListStyles(theme, themeName)}
                componentRef={listRef}
            />
        </div>
    );

    const onClick = (): void => addRecipient(listRef);

    return (
        <div className='recipient-list'>
            <Stack horizontal horizontalAlign='space-between'>
                <StackItem>
                    <Label
                        className='recipient-list-label'
                        styles={getSectionLabelStyles(themeName, theme)}
                        required={required}>
                        {recipient_list_label}
                    </Label>
                </StackItem>
                <StackItem>
                    <Toggle
                        inlineLabel
                        className='complete-in-order-toggle'
                        label={recipient_list_label_complete_in_order}
                        checked={completeInOrder}
                        onChange={toggleCompleteInOrder}
                    />
                </StackItem>
            </Stack>
            {recipientList}
            <ActionButton disabled={isAddRecipientDisabled} onClick={onClick} iconProps={{ iconName: 'AddFriend' }}>
                {recipient_list_button_text_add_recipient}
            </ActionButton>
        </div>
    );
};
